import { useEffect, useState } from "react";
import { usePermission } from "../../auth/access";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import {
  DEFAULT_TV_PLAYER,
  DEFAULT_TV_STREAMING_SLUG,
  isTvPlayerChoice,
  type PremiseConfig,
  type TvPlayerChoice,
} from "@shared/types/foyer";
import { foyerApi } from "../../http/foyer";
import { tokens } from "../../theme";
import { FieldBlock, SegmentedChoice } from "./StartNavLayout";

/**
 * What the fleet says is actually INSTALLED, for the "External TV app" option.
 *
 * The version is not a setting and cannot be one: the slug is global config,
 * but the version is whatever each panel happens to have, reported on its own
 * heartbeat. So this asks the fleet rather than inventing a field, and it is
 * deliberately allowed to answer "nothing".
 *
 * ⚠️ Android only (TIZEN-13). A Samsung panel has no shell to ask, so it
 * reports null there — which must read as "this platform cannot answer",
 * never as "the app is missing".
 */
function useReportedStreamingVersion(premiseId: string | undefined): {
  version: string | null;
  reporting: number;
  total: number;
} {
  const [state, setState] = useState({
    version: null as string | null,
    reporting: 0,
    total: 0,
  });
  useEffect(() => {
    if (!premiseId) return;
    let alive = true;
    void (async () => {
      try {
        const res = await foyerApi.fleetDevices({ premiseId });
        if (!alive) return;
        const devices = res?.devices ?? [];
        // Most prevalent wins. A property mid-rollout genuinely has two
        // versions, and showing the commonest is more useful than showing the
        // first one the query happened to return.
        const counts = new Map<string, number>();
        for (const d of devices) {
          const v = (d.live?.streamingAppVersion ?? "").trim();
          if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
        }
        let best: string | null = null;
        let bestN = 0;
        for (const [v, n] of counts) {
          if (n > bestN) {
            best = v;
            bestN = n;
          }
        }
        const reporting = [...counts.values()].reduce((a, b) => a + b, 0);
        setState({ version: best, reporting, total: devices.length });
      } catch {
        // A fleet read that fails must not break the drawer. The note simply
        // loses its version.
        if (alive) setState({ version: null, reporting: 0, total: 0 });
      }
    })();
    return () => {
      alive = false;
    };
  }, [premiseId]);
  return state;
}

const LABEL = {
  norigin: "tvPlayerSetting.players.norigin",
  foyer: "tvPlayerSetting.players.foyer",
} as const satisfies Record<TvPlayerChoice, string>;

/**
 * TV-6: "When a guest opens TV" - which player the TV item opens in every room
 * at this place (`PremiseConfig.tvPlayer`). Moved here from Location → Config
 * so it sits on the TV item it governs.
 *
 * 🚨 STAFF ONLY, as it was on the Config page: the built-in destination is still
 * in testing, and a premise flipped to it before its screens can play anything
 * gets an empty television. `canManageSettings` is `adminOnly`. Everyone else
 * sees the current choice, read-only.
 *
 * ⚠️ WHEN THE BUILT-IN PLAYER SHIPS: drop the "still in testing" sentence and
 * reconsider the gate.
 */
export default function TvPlayerSetting({
  premise,
  onChange,
}: {
  premise: PremiseConfig;
  onChange: (next: TvPlayerChoice) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const canManagePlatform = usePermission("premises", "administrate", {
    premiseId: premise.premiseId,
  });
  const reported = useReportedStreamingVersion(premise.premiseId);
  const value = isTvPlayerChoice(premise.tvPlayer)
    ? premise.tvPlayer
    : DEFAULT_TV_PLAYER;
  // The slug IS global config, injected into every premise read by
  // `api/foyer.ts`. The version is not, and is asked of the fleet above.
  const slug = premise.tvStreamingAppSlug?.trim() || DEFAULT_TV_STREAMING_SLUG;
  const external = reported.version
    ? t("tvPlayerSetting.externalWithVersion", {
        slug,
        version: reported.version,
        reporting: reported.reporting,
        total: reported.total,
      })
    : reported.total > 0
      ? // Deliberately not "the app is missing": Samsung panels cannot answer
        // at all, and saying so is the difference between a shrug and a truck
        // roll.
        t("tvPlayerSetting.externalNoVersion", { slug })
      : t("tvPlayerSetting.external", { slug });

  return (
    <FieldBlock
      label={t("tvPlayerSetting.label")}
      help={t("tvPlayerSetting.help")}
    >
      {canManagePlatform ? (
        <SegmentedChoice<TvPlayerChoice>
          value={value}
          options={[
            { value: "norigin", label: t(LABEL.norigin) },
            { value: "foyer", label: t(LABEL.foyer) },
          ]}
          onChange={onChange}
          testId="tv-player"
        />
      ) : (
        <Typography
          variant="body2"
          sx={{ fontWeight: "fontWeightSemibold" }}
          data-testid="tv-player-readonly"
        >
          {t(LABEL[value])}
        </Typography>
      )}
      <Box sx={{ mt: 1.5 }} data-testid="tv-player-note">
        <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
          {t("tvPlayerSetting.builtInNote")}
        </Typography>
        <Typography variant="bodySm" sx={{ mt: 0.5, color: tokens.inkMuted }}>
          {t("tvPlayerSetting.pushNote", { external })}
        </Typography>
        {!canManagePlatform && (
          <Typography variant="bodySm" sx={{ mt: 0.5, color: tokens.inkMuted }}>
            {t("tvPlayerSetting.staffOnly")}
          </Typography>
        )}
      </Box>
    </FieldBlock>
  );
}
